import type { ReactNode } from 'react';
import { cx } from '@/lib/cx';

/**
 * A list of values rendered as neutral pills that WRAP onto as many lines as
 * they need. Owner's decision 2026-09-25: every multi-value cell (cities,
 * regions, projects, products, services …) shows EVERY value as a pill, with
 * no cap and no "+N more" — a comma-joined string truncated by CSS was hiding
 * the tail, and the tail is exactly what a reader opened the column to see.
 *
 * ⚠️ The pill is the neutral `Badge` look, restated rather than reused:
 * `Badge` is `whitespace-nowrap`, and one long project name in a narrow column
 * would then overflow its cell. Here a pill may break inside itself, so the
 * row grows instead of the column spilling.
 *
 * ⛔ An EMPTY list renders `empty` (default an em dash) — never nothing, so a
 * blank cell cannot be mistaken for one that failed to render. What `[]`
 * MEANS is the caller's to say; pass a sentence as `empty` where a dash would
 * mislead.
 *
 * ⛔ Not for sentences, tooltips or the Google Sheets mirror — those stay comma
 * text (owner's decision). A `title` carrying the joined list is still the
 * caller's to keep where one existed.
 */
export interface ValuePillsProps {
  items: readonly { key: string | number; label: ReactNode; title?: string; muted?: boolean }[];
  empty?: ReactNode;
  className?: string;
  title?: string;
}

export function ValuePills({ items, empty = '—', className, title }: ValuePillsProps) {
  if (items.length === 0) {
    return <span className="text-fg-muted">{empty}</span>;
  }
  return (
    <span title={title} className={cx('flex min-w-0 flex-wrap gap-1', className)}>
      {items.map((item) => (
        <span
          key={item.key}
          title={item.title}
          className={cx(
            'inline-flex max-w-full items-center rounded-sm border border-border bg-transparent px-1.5 py-0.5 text-2xs font-medium text-fg-secondary break-words whitespace-normal',
            item.muted && 'opacity-60',
          )}
        >
          {item.label}
        </span>
      ))}
    </span>
  );
}

/** Convenience for the commonest shape: a plain list of names. */
export function namePills(names: readonly string[]): ValuePillsProps['items'] {
  return names.map((name, i) => ({ key: `${i}:${name}`, label: name }));
}
